import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, BehaviorSubject, Subject, EMPTY, from, of } from 'rxjs';
import { catchError, map, finalize, shareReplay } from 'rxjs/operators';
import * as moment from 'moment';
// Interfaces
import { SaveOptions } from '../interfaces/save-options';
import { UploadOptions } from '../interfaces/upload-options';
// Modèles
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { ModelBase } from 'src/app/@shared/models/abstract/model-base.model';
import { AuthorizedStatus } from 'src/app/@shared/models/core/authorized-status.model';
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';
import { HistoryEvent } from 'src/app/@shared/models/core/history-event.model';
import { File as FileModel } from 'src/app/@shared/models/file/file.model';
// Utilities
import { QueryOptions, QueryOptionsParam } from 'src/app/@shared/utilities/odata/query-params';
import { ODataListResponse, ODataResponse, ODataError } from 'src/app/@shared/utilities/odata/response';
import { MassDeleteResponse } from 'src/app/@shared/utilities/odata/mass-delete';
import { UploadProgress } from 'src/app/@shared/utilities/http/upload-progress';
import { serializeForApi } from 'src/app/@shared/decorators/api-field.decorator';
// Services
import { MessageServiceBroker } from './message.service';
import { Logger } from './logger/logger.service';

const LOGGER = new Logger("ServiceBase");

export interface NavigationParam {
    key: string,
    childClass: string | null
    path: string,
    resultCardinality?: 'one' | 'many'
}

export interface RouteParam {
    key: string,
    path: string
}

export class ServiceBase {

    // Behaviour subjects
    // Contient l'état actuel du service et permettent des appels HTTP ou des actions de l'application
    // 1. Objet contenant l'instance en cours de traitement
    protected _selected$: any = null;
    // 2. Évènements
    protected _archived$: Subject<any> = new Subject<any>();
    protected _authorizedStatusLoaded$: Subject<any> = new Subject<AuthorizedStatus[]>();
    protected _created$: Subject<any> = new Subject<any>();
    protected _deleted$: Subject<number> = new Subject<number>();
    protected _updated$: Subject<any> = new Subject<any>();
    protected _uploaded$: Subject<any> = new Subject<any>();
    protected _validated$: Subject<number> = new Subject<number>();
    // 3. Indicateurs d'activité
    protected _counting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _creating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _deleting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _downloading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _fetching$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _reading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _updating$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected _uploading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Obeservables pour les éléments définis au dessus
    public readonly selected$: any = null;
    public readonly archived$ = this._archived$.asObservable();
    public readonly authorizedStatusLoaded$ = this._authorizedStatusLoaded$.asObservable();
    public readonly counting$ = this._counting$.asObservable();
    public readonly created$ = this._created$.asObservable();
    public readonly creating$ = this._creating$.asObservable();
    public readonly deleted$ = this._deleted$.asObservable();
    public readonly deleting$ = this._deleting$.asObservable();
    public readonly downloading$ = this._downloading$.asObservable();
    public readonly fetching$ = this._fetching$.asObservable();
    public readonly reading$ = this._reading$.asObservable();
    public readonly updated$ = this._updated$.asObservable();
    public readonly updating$ = this._updating$.asObservable();
    public readonly uploaded$ = this._uploaded$.asObservable();
    public readonly uploading$ = this._uploading$.asObservable();
    public readonly validated$ = this._validated$.asObservable();

    // Services
    protected httpClient: HttpClient;
    protected messageService: MessageServiceBroker;

    // Indicateurs de progrès
    private _progressArray$: BehaviorSubject<UploadProgress[]> = new BehaviorSubject<UploadProgress[]>([]);
    public readonly progressArray$: Observable<UploadProgress[]> = this._progressArray$.asObservable();

    // Routes
    protected routes: any = {};
    protected navigations: any = {};

    // Gestion du cache
    protected useCache: boolean = false;
    protected cacheDuration: number = 60 * 60 * 24 * 1000; // 24 heures
    protected cachedQueries: { [key: string]: { request: Observable<any | any[]>, expiry: number } } = {};

    constructor(
        protected className: string,
        protected endpoint: string
    ) {
        this.httpClient = inject(HttpClient);
        this.messageService = inject(MessageServiceBroker);
        // Éléments sélectionné, cela doit être défini dynamiquement
        this._selected$ = new BehaviorSubject<any>(null);
        this.selected$ = this._selected$.asObservable();

        // Construction des routes par défaut
        this.routes = {
            archive: this.endpoint + `{id}/archive/`,
            authorizedStatus: this.endpoint + `authorized-status/`,
            create: this.endpoint,
            delete: this.endpoint + `{id}/`,
            list: this.endpoint,
            next: this.endpoint + `{id}/next/`,
            previous: this.endpoint + `{id}/previous/`,
            single: this.endpoint + `{id}/`,
            update: this.endpoint + `{id}/`
        };

        // Construction des propriétés de navigation par défaut
        this.navigations = {
            attachments: {
                childClass: 'File',
                path: 'attachments/',
                resultCardinality: 'many'
            },
            conversations: {
                childClass: 'Conversation',
                path: 'conversations/',
                resultCardinality: 'many'
            },
        }
    }

    /**
     * Méthode d'ajout des propriétés de navigations
     */
    public addNavigations(navigations: NavigationParam[]): void {
        for (let i = 0; i < navigations.length; i++) {
            this.navigations[navigations[i].key] = {
                childClass: navigations[i].childClass,
                path: navigations[i].path,
                resultCardinality: navigations[i].resultCardinality || 'many'
            }
        }
    }

    /**
     * Méthode d'ajout d'une route aux routes par défaut
     */
    public addRoutes(routes: RouteParam[]): void {
        for (let i = 0; i < routes.length; i++) {
            this.routes[routes[i].key] = this.endpoint + routes[i].path;
        }
    }

    /**
     * Méthode qui appelle le service d'archivage d'un objet
     * @param {any} instance L'objet à archiver
     */
    public archive(instance: ModelBase, options?: SaveOptions) {
        // Options par défaut
        options = this.fillDefaultSaveOptions(options);
        // Construction de l'URL
        options.url = this.routes.archive.replace('{id}', instance.id.toString());
        // Appel de la méthode update
        this.update$(instance, options).subscribe(() => {
            this._archived$.next(instance);
        });
    }

    /**
     * Méthode qui déclenche le téléchargement groupé des pièces jointes d'un objet porteur.
     * L'archive est toujours produite, y compris pour une seule pièce jointe.
     * Le nom de l'archive est laissé au serveur (`documents_<AAAA-MM-JJ>.zip`).
     * @param {any} parent L'objet qui porte les pièces jointes
     * @param {number[]} ids Les identifiants des pièces jointes demandées
     * @param {string} navigation La collection de pièces jointes de l'objet porteur
     */
    public attachmentsMassDownload$(parent: any, ids: number[], navigation: string = 'attachments'): Observable<HttpResponse<Blob>> {
        let url = this.routes['single'].replace('{id}', parent.id.toString()) + this.navigations[navigation].path + 'mass-download/';
        return this._massDownload$(url, ids);
    }

    /**
     * Méthode de nettoyage des données avant envoi à l'API.
     * Supprime les métadonnées front-end et applique les décorateurs @OmitField / @FkField
     * définis sur le modèle et toute sa chaîne d'héritage.
     * Peut être surchargée pour des suppressions supplémentaires propres à un service.
     */
    protected cleanData(data: any, instance?: any): any {
        // Métadonnées front-end sans getter TypeScript
        delete data.errors;
        delete data.status;
        delete data.undefined;
        // Alléger __meta__ : ne garder que les 3 flags utiles au backend
        if (data.__meta__) {
            data.__meta__ = {
                new: data.__meta__.new,
                deleted: data.__meta__.deleted,
                has_changed: data.__meta__.hasChanged,
            };
        }

        if (instance?.constructor) {
            return serializeForApi(data, instance.constructor);
        }
        return data;
    }

    /**
     * Méthode qui supprime les requêtes en cache
     */
    public clearCache() {
        this.cachedQueries = {};
    }

    /**
     * Appel du service de comptage d'une collection
     */
    public count$(options?: QueryOptions): Observable<any> {
        // URL
        let queryOptions = new QueryOptions(this.routes.list, options);
        let queryUrl = queryOptions.toUrlString() + '/$count';

        // Activation de l'indicateur d'activité
        this._counting$.next(queryOptions.activityIndicator);
        return this.httpClient.get(queryUrl).pipe(
            finalize(() => {
                this._counting$.next(false);
            }),
            map((count: any) => {
                return count;
            })
        );
    }

    /**
     * Appel du service de création de l'objet
     * @param instance L'objet à sauvegarder
     */
    protected create$(instance: ModelBase, options?: SaveOptions): Observable<any> {
        options = this.fillDefaultSaveOptions(options);
        // Indiquer à l'application que le service est en train de créer un objet
        this._creating$.next(true);
        instance.busy = true;

        let url = !!options.url ? options.url : this.routes.create;

        return this.httpClient.post(url, this._toApi(instance)).pipe(
            finalize(() => {
                instance.busy = false;
                this._creating$.next(false);
            }),
            map((body: any) => {
                let response = new ODataResponse(body);
                if (options?.showMessages) {
                    // Afficher les messages reçus
                    this.messageService.sendODataMessages(response.messages);
                }
                // Création d'une instance de l'objet
                instance = (GeneralFactory.get(!!options?.resultClass ? options.resultClass : this.className, response.value) as ModelBase);
                // Envoyer un évènement pour informer l'application de la création réussie de l'objet
                if (!!options?.emitEvent) {
                    this._created$.next(instance);
                }
                // Sélectionner l'instance pour la rendre disponible dans l'application
                if (!!options?.select) {
                    this.select(instance);
                }
                // Retourner l'instance
                return instance;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error, instance, options);
                    return of(false);
                }
            )
        );
    }

    /**
     * Appel de la méthode de création, sans nécessiter de souscrire à l'observable
     * @param instance L'objet à sauvegarder
     */
    protected create(instance: ModelBase, options?: SaveOptions) {
        this.create$(instance, this.fillDefaultSaveOptions(options)).subscribe(() => { });
    }

    /**
     * Appel du service de création de plusieurs objets
     * @param instances Les objets à sauvegarder
     */
    protected createBulk$(instances: ModelBase[], options?: SaveOptions): Observable<any> {
        options = this.fillDefaultSaveOptions(options);
        // Indiquer à l'application que le service est en train de créer un objet
        this._creating$.next(true);

        let url = !!options.url ? options.url : this.routes.create;

        return this.httpClient.post(url, this._toApi(instances)).pipe(
            finalize(() => {
                this._creating$.next(false);
            }),
            map((body: any) => {
                let response = new ODataResponse(body);
                if (options?.showMessages) {
                    // Afficher les messages reçus
                    this.messageService.sendODataMessages(response.messages);
                }
                // Envoyer un évènement pour informer l'application de la création réussie des objets
                if (!!options?.emitEvent) {
                    this._created$.next(instances);
                }
                // Retourner l'instance
                return true;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    /**
     * Crée un éléments lié à un objet
     * @param {any} instance Objet à créer
     * @param {any} parent Objet parent
     * @param {string} navName Propriété de navigation
     */
    public createRelated$(instance: any, parent: any, navName: string, options?: SaveOptions): Observable<any> {
        // Complete options
        let url = this.routes['single'].replace('{id}', parent.id.toString()) + this.navigations[navName].path;
        options = this.fillDefaultSaveOptions(options);
        options.resultClass = this.navigations[navName].childClass;
        options.emitEvent = false;
        options.select = false;
        options.url = url;
        return this.create$(instance, options).pipe(
            map(instance => {
                this._updated$.next(parent);
                return instance;
            })
        );
    }

    /**
     * Crée un éléments lié à un objet, sans souscription
     * @param {any} instance Objet à créer
     * @param {any} parent Objet parent
     * @param {string} navName Propriété de navigation
     */
    public createRelated(instance: any, parent: any, navName: string, options?: SaveOptions) {
        try {
            this.createRelated$(instance, parent, navName, options).subscribe(() => { });
        } catch (_) { }
    }

    /**
     * Appel du service de suppression de l'objet
     * @param instance L'objet à supprimer
     */
    private _delete$(instance: ModelBase, options?: SaveOptions): Observable<boolean> {
        if (!instance?.id && !instance?.sid) {
            // Aucun ID de fourni
            return of(false);
        }
        // Options par défaut
        options = this.fillDefaultSaveOptions(options);
        // Indicate to the application that service is deleting an object
        // Indicateurs d'activité
        this._deleting$.next(true);
        instance.busy = true;

        const id = instance.id ? instance.id.toString() : instance.sid;
        let url = this.routes.delete.replace('{id}', encodeURIComponent(id));

        // Appel HTTP
        return this.httpClient.delete(url, { body: this._toApi(instance) }).pipe(
            finalize(() => {
                instance.busy = false;
                this._deleting$.next(false);
            }),
            map((body: any) => {
                if (!!body) {
                    let response = new ODataResponse(body);
                    if (options?.showMessages) {
                        // Afficher les messages de la réponse
                        this.messageService.sendODataMessages(response.messages);
                    }
                }
                // Évènement pour informer l'application de la suppression de l'objet
                this._deleted$.next(instance.id);
                return true;
            }),
            catchError(
                (error: any) => {
                    // Manage error
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    /**
     * Suppression, sans besoin de souscrire à l'observable
     * @param instance L'objet à supprimer
     */
    private _delete(instance: ModelBase, options?: SaveOptions) {
        this._delete$(instance, this.fillDefaultSaveOptions(options)).subscribe(() => { });
    }

    /**
     * Retire une pièce jointe de son objet porteur.
     *
     * ⚠️ Le geste se demande **au document**, jamais au fichier : `/api/files/` est en
     * lecture seule depuis `#1052`, et un `PATCH` avec `deleted: true` y répond 405.
     * C'est le couple (document, fichier) qui autorise le retrait, l'id de fichier seul
     * ne prouve rien.
     *
     * Retirer n'est pas supprimer : le serveur **détache** la pièce, et ne supprime le
     * fichier que s'il ne reste la pièce d'aucun autre document.
     *
     * @param {any} parent L'objet qui porte la pièce jointe
     * @param {number} fileId L'identifiant de la pièce jointe à retirer
     * @param {string} navigation La collection de pièces jointes de l'objet porteur
     */
    public detachAttachment$(parent: any, fileId: number, navigation: string = 'attachments'): Observable<any> {
        let url = this.routes['single'].replace('{id}', parent.id.toString())
            + this.navigations[navigation].path + fileId.toString() + '/';
        return this._deleteAction$(url);
    }

    /**
     * Méthode de dévalidation d'un objet, c'est en fait un proxy qui appel la
     * méthode de mise à jour avec une autre URL
     * @param {any} instance L'objet à valider
     */
    public devalidate$(instance: ModelBase, options?: SaveOptions): Observable<any> {
        options = this.fillDefaultSaveOptions(options);
        // URL à utiliser
        options.url = this.routes.devalidate.replace('{id}', instance.id.toString());
        // Appel de la mise à jour
        return this.update$(instance, options).pipe(
            map(instance => {
                this._validated$.next(instance.id);
                return instance;
            })
        );
    };

    /**
     * Télécharge un fichier et renvoie un Blob
     * @param id Identifiant du fichier ou de l'objet auquel le fichier est lié.
     * Cet identifiant viendra en remplacement du marqueur sur l'URI.
     * Une route 'downloadFile' doit exister sur le service
     *
     * ⚠️ L'échec passe par `handleError()`, comme partout ailleurs. Sans lui, un refus
     * serveur — un 404 sur un fichier hors périmètre depuis `#1052` — ne produisait
     * strictement rien à l'écran : l'intercepteur global se contente de relayer
     * l'erreur, et aucun appelant ne pose de callback d'erreur.
     */
    public downloadFile$(id: number, emitEvent: boolean = true): Observable<Blob> {
        let url = this.routes.downloadFile.replace('{id}', id.toString());
        if (emitEvent) {
            this._downloading$.next(true);
        }
        return this.httpClient.get(url, {
            responseType: 'blob'
        }).pipe(
            finalize(() => {
                if (emitEvent) {
                    this._downloading$.next(false)
                }
            }),
            map(response => { return response }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return EMPTY;
                }
            )
        );
    }

    /**
     * Télécharge un fichier depuis une URL en utilisant l'API Fetch et retourne une Observable.
     * @param url L'URL du fichier à télécharger.
     * @returns Une Observable résolvant avec le contenu du fichier sous forme de Blob.
     */
    public downloadFromUrl$(url: string, emitEvent: boolean = true): Observable<Blob> {
        if (emitEvent) {
            this._downloading$.next(true);
        }
        return new Observable(observer => {
            fetch(url)
                .then(response => {
                    if (emitEvent) {
                        this._downloading$.next(false)
                    }
                    if (!response.ok) {
                        throw new Error(`Erreur de téléchargement : ${response.statusText}`);
                    }
                    return response.blob();
                })
                .then(blob => {
                    observer.next(blob);
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                });
        });
    }

    /**
     * Méthode permettant de lancer un évènement depuis l'extérieur de la classe
     *
     * Pour l'instant ce n'est utilisé que pour les évènements de création, dans le cas où la création
     * aura été faite dans le programme appelant par la méthode createRelated
     */
    public emitEvent(event: string, data: any) {
        switch (event) {
            case 'created':
                this._created$.next(data);
                break;
            default:
                break;
        }
    }

    public export$(options?: QueryOptionsParam): Observable<HttpResponse<Blob>> {
        let queryOptions = new QueryOptions(this.routes.export, options);
        let url = queryOptions.toUrlString();
        if (queryOptions.activityIndicator) {
            this._downloading$.next(true);
        }
        return this.httpClient.get(url, {
            observe: 'response',
            responseType: 'blob'
        }).pipe(
            finalize(() => {
                if (queryOptions.activityIndicator) {
                    this._downloading$.next(false)
                }
            })
        );
    }

    /**
     * Remplissage des options par défaut
     * @param {SaveOptions} options
     * @return {SaveOptions} Les options complétées
     */
    protected fillDefaultSaveOptions(options?: SaveOptions): SaveOptions {
        options = !!options ? options : {};
        options.showMessages = typeof options.showMessages === 'undefined' ? true : options.showMessages;
        options.emitEvent = typeof options.emitEvent === 'undefined' ? true : options.emitEvent;
        options.select = typeof options.select === 'undefined' ? true : options.select;
        options.callbacks = typeof options.callbacks === 'undefined' ? [] : options.callbacks;
        options.resultClass = options?.resultClass;

        return options;
    }

    protected fillDefaultUploadOptions(options?: UploadOptions): UploadOptions {
        options = !!options ? options : {};
        options.async = typeof options.async === 'undefined' ? false : options.async;
        options.emitEvent = typeof options.emitEvent === 'undefined' ? true : options.emitEvent;
        options.showMessages = typeof options.showMessages === 'undefined' ? true : options.showMessages;
        options.successMessage = typeof options.successMessage === 'undefined' ? undefined : options.successMessage;

        return options;
    }

    /**
     * Méthode qui retourne une seule entité lue sur le serveur
     * @param {number} id Instance ID
     * @param {boolean} [select=true] Faut-il sélectionner directement l'entité ?
     */
    public get$(id: number, select: boolean = true, options?: QueryOptionsParam): Observable<any> {
        // Vérification qu'un ID est fourni
        if (!id) {
            // Non fourni
            return of(null);
        }
        // Indicateur d'activité
        this._reading$.next(true);

        let url = this.routes.single.replace('{id}', id.toString());
        if (!!options) {// URL
            let queryOptions = new QueryOptions(url, options);
            url = queryOptions.toUrlString();
        }
        return this.httpClient.get(url).pipe(
            finalize(() => {
                this._reading$.next(false);
            }),
            map((result: any) => {
                // Instanciation de l'objet lu
                let instance = (GeneralFactory.get(this.className, result) as ModelBase);
                if (select) {
                    // Select the instance
                    this.select(instance);
                }
                return instance;
            }),
            catchError(
                (error: any) => {
                    // Manage error
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    /**
     * Lecture d'un objet sans besoin de souscrire
     * @param {number} id Instance ID
     * @param {boolean} [select=true] Faut-il sélectionner directement l'entité ?
     */
    public get(id: number, select: boolean = true) {
        this.get$(id, select).subscribe(() => { });
    }

    public getAttachments$(instance: any, navigation: string = 'attachments', options?: QueryOptionsParam): Observable<FileModel[]> {
        // Complete options
        return this.getRelated$(instance, navigation, options);
    }

    public getAuthorizedStatus$(options?: QueryOptionsParam): Observable<any | any[]> {
        // URL
        let queryOptions = new QueryOptions(this.routes.authorizedStatus, options);
        let queryUrl = queryOptions.toUrlString();

        return this.httpClient
            .get(queryUrl)
            .pipe(
                map((json: any) => {
                    let response = new ODataListResponse(json);
                    // Construction des instances
                    let instances: AuthorizedStatus[] = [];
                    for (let result of response.results) {
                        instances.push(new AuthorizedStatus(result));
                    }
                    // Remplacement des résultats bruts par les instances
                    response.results = instances;
                    this._authorizedStatusLoaded$.next(instances);
                    return instances;
                }),
                catchError(
                    (error: any) => {
                        // Manage error
                        this.handleError(error);
                        return of(false);
                    }
                )
            )
    }

    public getConversations$(instanceId: any, navigation: string = 'conversations', options?: QueryOptionsParam): Observable<Conversation[]> {
        // Complete options
        return this.getRelated$({ id: instanceId }, navigation, options);
    }

    /**
     * Lecture de la timeline d'historique d'un objet via l'endpoint générique
     * `{id}/history/` (hérité par toutes les ViewSets).
     * Réponse : tableau à plat trié par date décroissante, paginable via $top/$skip.
     * Contrat : b4cash-srv/documentation/architecture/OBJECT_HISTORY_API.md
     * @param {number} id Instance ID
     * @param {QueryOptionsParam} [options] Options de pagination (top/skip)
     */
    public getHistory$(id: number, options?: QueryOptionsParam): Observable<HistoryEvent[]> {
        // URL
        let url = this.routes.single.replace('{id}', id.toString()) + 'history/';
        let queryOptions = new QueryOptions(url, options);
        let queryUrl = queryOptions.toUrlString();

        return this.httpClient.get(queryUrl).pipe(
            map((results: any) => {
                // Construction des instances (tableau à plat, pas d'enveloppe OData)
                return (results || []).map((result: any) => new HistoryEvent(result));
            }),
            catchError(
                (error: any) => {
                    // Gestion des erreurs
                    this.handleError(error);
                    return of([]);
                }
            )
        );
    }

    /**
     * Renvoie une collection d'objets
     * @param {QueryOptions} options Paramètres de requête
     */
    public getList$(options?: QueryOptionsParam): Observable<any | any[]> {
        // URL
        let queryOptions = new QueryOptions(this.routes.list, options);
        let queryUrl = queryOptions.toUrlString();

        if (this.cachedQueries[queryUrl] && this.cachedQueries[queryUrl].expiry > Date.now()) {
            return this.cachedQueries[queryUrl].request;
        }

        // Indicateur d'activité
        this._fetching$.next(queryOptions.activityIndicator);
        // Appel du service
        const request$ = this.httpClient
            .get(queryUrl)
            .pipe(
                finalize(() => {
                    this._fetching$.next(false);
                }),
                map((json: any) => {
                    let response = new ODataListResponse(json);
                    // Construction des instances
                    let data: any | any[];
                    if (queryOptions.resultCardinality === 'many') {
                        data = [];
                        for (let result of response.results) {
                            if (!!queryOptions.resultClass || queryOptions.resultClass === undefined) {
                                data.push(GeneralFactory.get(!!queryOptions.resultClass ? queryOptions.resultClass : this.className, result));
                            } else {
                                data.push(result);
                            }
                        }
                    } else {
                        // Ce cas se produire dans le cas des navigations entre objets
                        data = GeneralFactory.get(!!queryOptions.resultClass ? queryOptions.resultClass : this.className, response.results);
                    }
                    if (queryOptions.count) {
                        return {
                            count: response.count,
                            results: data
                        };
                    } else {
                        return data;
                    }
                }),
                catchError(
                    (error: any) => {
                        // Gestions des erreurs
                        try {
                            this.handleError(error);
                        } catch (_) { }
                        // Retourner une valeur par défaut
                        if (queryOptions.count) {
                            return of({
                                count: 0,
                                results: []
                            });
                        } else {
                            return of(queryOptions.resultCardinality === 'many' ? [] : null);
                        }
                    }
                ),
                shareReplay(1)
            );

        // Mise en cache
        if (this.useCache) {
            this.cachedQueries[queryUrl] = {
                request: request$,
                expiry: Date.now() + this.cacheDuration
            };
        }

        return request$;
    }

    /**
     * Renvoie une collection d'objets, sans souscrire
     * @param {QueryOptions} options Paramètres de requête
     */
    public getList(options?: QueryOptionsParam) {
        this.getList$(options).subscribe(() => { });
    }

    /**
     * Retrouve les données d'une collection à partir de l'objet parent
     * @param {any} parent Objet parent
     * @param {string} navName Propriété de navigation
     */
    public getRelated$(parent: any, navName: string, optionsIn?: QueryOptionsParam) {
        // Complete options
        let url = '';
        try {
            url = this.routes['single'].replace('{id}', parent.id.toString()) + this.navigations[navName].path;
        } catch (e) {
            url = this.routes['single'].replace('{id}', parent.code.toString()) + this.navigations[navName].path;
        }
        let options = new QueryOptions(url, optionsIn);
        options.resultClass = this.navigations[navName].childClass;
        options.resultCardinality = this.navigations[navName].resultCardinality || 'many';
        if (!optionsIn?.activityIndicator) {
            options.activityIndicator = false;
        }
        return this.getList$(options);
    }

    /**
     * Retrouve les données d'une collection à partir de l'objet parent, sans souscription
     * @param {any} parent Objet parent
     * @param {string} navName Propriété de navigation
     */
    public getRelated(navName: string, parent: any, options?: QueryOptionsParam) {
        try {
            this.getRelated$(parent, navName, options).subscribe(() => { });
        } catch (_) { }
    }

    /**
     * Méthode générique de gestion des erreurs
     *
     * Elle transforme l'erreur HTTP standard reçue en un format unique, et la propage
     *
     * @param {any} error L'objet d'erreur
     * @param {ModelBase} instance L'instance envoyée, quand il y en a une : le contexte que le serveur n'a pas
     * @param {SaveOptions} options Les options de l'appel : `showErrors: false` laisse l'affichage à l'appelant
     * @throws {ODataError} L'erreur au format ODataError. L'exception est toujours lancée !
     */
    protected handleError(e: any, instance?: ModelBase, options?: SaveOptions) {
        let error = ODataError.fromResponse(e, instance);
        if (options?.showErrors !== false) {
            this.messageService.sendODataMessages(error.messages);
        }
        throw error;
    }

    /**
     * Méthode qui informe si un objet est sélectionné
     * @returns {boolean}
     */
    public hasSelection(): boolean {
        return !!this._selected$.getValue();
    }

    public massDelete$(instances: ModelBase[], options?: SaveOptions): Observable<null> {
        options = this.fillDefaultSaveOptions(options);
        // Indicate to the application that service is creating an object
        this._deleting$.next(true);
        // Les indicateurs d'activité de toutes les instances sont à mettre à jour
        for (let i = 0; i < instances.length; i++) {
            instances[i].busy = true;
        }

        // L'url doit obligatoirement est renseignée dans les options
        if (!options.url) {
            throw new Error(`L'URL de suppression doit être renseignée`);
        }

        // Conversion des instances pour l'API
        let data = [];
        for (let i = 0; i < instances.length; i++) {
            data.push(this._toApi(instances[i]));
        }

        return this.httpClient.post(options.url, data).pipe(
            finalize(() => {
                this._deleting$.next(false);
            }),
            map((body: any) => {
                if (!!body) {
                    let response = new ODataResponse(body);
                    if (options?.showMessages) {
                        // Afficher les messages reçus
                        this.messageService.sendODataMessages(response.messages);
                    }
                }
                return null;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(null);
                }
            )
        );
    }

    /**
     * Supprime une sélection d'objets en **une seule demande**.
     *
     * ⚠️ À ne pas confondre avec {@link massDelete$}, qui poste des instances complètes sur
     * une route dédiée d'un module. Ici, on ne poste que des ids, sur la route générique
     * `mass-delete/` qu'expose le `MassDeleteMixin` du back.
     *
     * C'est le remplaçant de la boucle « un `DELETE` par ligne » : au-delà de 240 requêtes
     * par minute, le plafond de débit de l'API rejetait la majorité de la sélection en
     * `HTTP 429`, sans qu'aucun message ne le signale (cf. #1075).
     *
     * Le serveur répond `200` même quand tout est refusé — un refus est un résultat, pas
     * une erreur de la demande. La synthèse n'est pas notifiée ici : c'est l'appelant qui
     * la compose, à partir de {@link MassDeleteResponse}.
     *
     * @param {number[]} ids Les ids des objets à supprimer, dans l'ordre de la sélection
     * @return {Observable<MassDeleteResponse | null>} Le rapport du serveur, objet par objet
     */
    public massDeleteSelection$(ids: number[]): Observable<MassDeleteResponse | null> {
        this._deleting$.next(true);

        return this.httpClient.post(this.routes.massDelete, { ids: ids }).pipe(
            finalize(() => {
                this._deleting$.next(false);
            }),
            map((body: any) => new MassDeleteResponse(body)),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(null);
                }
            )
        );
    }

    /**
     * Méthode qui appelle le service de téléchargement groupé des PDF de plusieurs documents.
     * L'archive ZIP est streamée par le serveur : les documents dépourvus de PDF ne font pas
     * échouer la demande, ils sont listés dans une entrée `_documents_manquants.txt` de l'archive.
     * @param {number[]} ids Les ids des documents dont le PDF est demandé
     * @param {string} fileName Le nom de l'archive
     * @return {Observable<HttpResponse<Blob>>} La réponse contenant l'archive
     */
    public massDownload$(ids: number[], fileName: string): Observable<HttpResponse<Blob>> {
        return this._massDownload$(this.routes.massDownload, ids, fileName);
    }

    /**
     * Méthode générique de téléchargement groupé : poste une sélection d'identifiants sur une
     * route de téléchargement en masse et restitue l'archive streamée par le serveur.
     * @param {string} url La route de téléchargement groupé
     * @param {number[]} ids Les identifiants des objets demandés
     * @param {string} [fileName] Le nom de l'archive — à défaut, celui choisi par le serveur
     */
    private _massDownload$(url: string, ids: number[], fileName?: string): Observable<HttpResponse<Blob>> {
        this._downloading$.next(true);

        let body: any = { ids: ids };
        if (!!fileName) {
            body.fileName = fileName;
        }

        return this.httpClient.post(url, body, {
            observe: 'response',
            responseType: 'blob'
        }).pipe(
            finalize(() => {
                this._downloading$.next(false);
            }),
            catchError(
                (error: any) => {
                    // La réponse étant attendue en Blob, le corps d'une erreur en est un également :
                    // il faut le décoder pour retrouver les messages de l'API et les afficher.
                    let body$: Observable<string> = error.error instanceof Blob ? from((error.error as Blob).text()) : of('');
                    return body$.pipe(
                        map((text: string) => {
                            let messages: any = null;
                            try {
                                messages = JSON.parse(text);
                            } catch {
                                messages = null;
                            }
                            this.handleError({ ...error, error: !!messages ? messages : error.error });
                            return null as any;
                        })
                    );
                }
            )
        );
    }

    public massUpdate$(instances: ModelBase[], options?: SaveOptions): Observable<null> {
        options = this.fillDefaultSaveOptions(options);
        // Indicate to the application that service is creating an object
        this._updating$.next(true);
        // Les indicateurs d'activité de toutes les instances sont à mettre à jour
        for (let i = 0; i < instances.length; i++) {
            instances[i].busy = true;
        }

        // L'url doit obligatoirement est renseignée dans les options
        if (!options.url) {
            throw new Error(`L'URL de mise à jour doit être renseignée`);
        }

        // Conversion des instances pour l'API
        let data = [];
        for (let i = 0; i < instances.length; i++) {
            data.push(this._toApi(instances[i]));
        }

        return this.httpClient.post(options.url, data).pipe(
            finalize(() => {
                this._updating$.next(false);
            }),
            map((body: any) => {
                if (!!body) {
                    let response = new ODataResponse(body);
                    if (options?.showMessages) {
                        // Afficher les messages reçus
                        this.messageService.sendODataMessages(response.messages);
                    }
                }
                return null;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(null);
                }
            )
        );
    }

    /**
     * Méthode qui appelle l'instance suivante d'un objet, sans souscrire
     */
    public next(instance: ModelBase) {
        this.next$(instance).subscribe(() => { });
    }

    /**
     * Méthode qui appelle l'instance suivante d'un objet
     * L'instance suivante est celle qui a été créée après l'instance actuelle
     */
    public next$(instance: ModelBase): Observable<any> {
        // Construction de l'URL
        let url = this.routes.next.replace('{id}', instance.id.toString());
        return this.httpClient.get(url).pipe(
            finalize(() => {
                this._reading$.next(false);
            }),
            map((result: any) => {
                // Instanciation de l'objet lu
                let instance = (GeneralFactory.get(this.className, result) as ModelBase);
                return instance;
            })
        );
    }

    /**
     * Méthode qui appelle l'instance précédente d'un objet, sans souscrire
     */
    public previous(instance: ModelBase) {
        this.previous$(instance).subscribe(() => { });
    }

    /**
     * Méthode qui appelle l'instance précédente d'un objet
     * L'instance précédente est celle qui a été créée avant l'instance actuelle
     */
    public previous$(instance: ModelBase): Observable<any> {
        // Construction de l'URL
        let url = this.routes.previous.replace('{id}', instance.id.toString());
        return this.httpClient.get(url).pipe(
            finalize(() => {
                this._reading$.next(false);
            }),
            map((result: any) => {
                // Instanciation de l'objet lu
                let instance = (GeneralFactory.get(this.className, result) as ModelBase);
                return instance;
            })
        );
    }

    public reload() {
        let id = this._selected$.getValue().id;
        this.unselect();
        setTimeout(() => {
            this.get(id);
        }, 10);
    }

    /**
     * Méthode générique de sauvegarde d'un objet.
     * Elle va détecter quelle opération CRUD doit être effectuée
     * @param {any} instance L'instance à sauvegarder
     */
    public save$(instance: ModelBase, options?: SaveOptions): Observable<any> {
        // Vérification des flags
        if (instance.new) {
            // Cette instance est nouvelle, appel de la méthode de création
            return this.create$(instance, this.fillDefaultSaveOptions(options));
        } else if (instance.deleted) {
            // L'instance est marquée pour suppression
            return this._delete$(instance, this.fillDefaultSaveOptions(options));
        } else {
            // Mise à jour
            return this.update$(instance, this.fillDefaultSaveOptions(options));
        }
    }

    /**
     * Méthode générique de sauvegarde d'un objet, sans souscription
     * Elle va détecter quelle opération CRUD doit être effectuée
     * @param {any} instance L'instance à sauvegarder
     */
    public save(instance: ModelBase, options?: SaveOptions) {
        // Check instance flags
        if (instance.new) {
            // Cette instance est nouvelle, appel de la méthode de création
            this.create(instance, this.fillDefaultSaveOptions(options));
        } else if (instance.deleted) {
            // L'instance est marquée pour suppression
            this._delete(instance, this.fillDefaultSaveOptions(options));
        } else {
            // Mise à jour
            this.update(instance, this.fillDefaultSaveOptions(options));
        }
    }

    /**
     * Méthode de sélection d'un objet et d'information de l'application
     * @param {MainObject} instance L'objet (dé)sélectionné
     * @param {boolean} selected VRAI pour sélectionner, FAUX pour désélectionner
     */
    public select(instance: ModelBase, selected: boolean = true) {
        // Check that an instance is provided
        if (!instance) {
            return;
        }
        // Marqueur de sélection
        instance.selected = selected;
        if (selected) {
            this._selected$.next(instance);
        } else {
            // This is equivalent to unselecting object
            this._selected$.next(null);
        }
    }

    /**
     * Transformation d'une instance en objet pouvant être envoyé à l'API
     * Cela consiste principalement à enlever les _ au début des attributs
     * @param instance
     */
    protected _toApi(instance: any): any {
        let data: any = {};
        Object.entries(instance)
            .forEach(([key, value]) => {
                if (!([
                    '__meta__',
                    '_authorizedActivities',
                    '_status'
                ].includes(key))) {
                    if (value instanceof Array) {
                        let keyTmp = key[0] === '_' ? key.substring(1) : data[key];
                        data[keyTmp] = [];
                        for (let i = 0; i < value.length; i++) {
                            if (value[i] instanceof Object && !moment.isMoment(value[i])) {
                                data[keyTmp].push(this._toApi(value[i]));
                            } else {
                                data[keyTmp].push(value[i]);
                            }
                        }
                    } else {
                        if (value instanceof Object && !moment.isMoment(value)) {
                            value = this._toApi(value);
                        }
                        if (key[0] === '_') {
                            data[key.substring(1)] = value;
                        } else {
                            data[key] = value;
                        }
                    }
                } else if (key === '__meta__') {
                    data[key] = value;
                }
            })

        data = this.cleanData(data, instance);

        return data;
    }

    /**
     * Désélection d'un objet
     * @param {boolean} async Faut-il le faire en asynchrone ?
     */
    public unselect(async: boolean = false) {
        if (async) {
            setTimeout(
                () => {
                    this.unselect();
                },
                1
            );
        } else {
            this.select(this._selected$.getValue(), false);
        }
    }

    /**
     * Appel du service de mise à jour
     * @param {MainObject} instance Instance à mettre à jour
     */
    protected update$(instance: ModelBase, options?: SaveOptions): Observable<any> {
        options = this.fillDefaultSaveOptions(options);
        // On vérifie qu'on a asser d'info pour effectuer l'action
        if (!instance?.id && !!options?.url) {
            // Pas d'identifiant fourni
            return of(null);
        }
        // Indicateurs d'activité
        this._updating$.next(true);
        instance.busy = true;

        // Ne pas envoyer les données des médias liés
        if (!!instance.file && !!instance.file.mediaBase64) {
            instance.file.mediaBase64 = '';
        }

        let url = !!options.url ? options.url : this.routes.update.replace('{id}', instance.id.toString());

        // Appel du service
        return this.httpClient.put(url, this._toApi(instance)).pipe(
            finalize(() => {
                instance.busy = false;
                this._updating$.next(false);
            }),
            map((body: any, index: number) => {
                // Si la réponse est vide (cas HTTP 202 sans corps), retourner null
                if (body === null || body === undefined) {
                    return null;
                }
                let response = new ODataResponse(body);
                if (options?.showMessages) {
                    // Affichage des messages du serveur
                    this.messageService.sendODataMessages(response.messages);
                }
                // Information de l'application
                instance = (GeneralFactory.get(this.className, response.value) as ModelBase);
                this._updated$.next(instance);
                if (!!options?.select) {
                    this.select(instance);
                }
                // Appel des callbacks
                if (!!options?.callbacks) {
                    for (let i = 0; i < options?.callbacks.length; i++) {
                        options?.callbacks[i]();
                    }
                }
                // Retour de l'instance
                return instance;
            }),
            catchError(
                (error: any) => {
                    // Handle errors
                    this.handleError(error, instance);
                    return of(false);
                }
            )
        );
    }

    /**
     * Appel du service de mise à jour, sans souscription
     * @param {MainObject} instance Instance à mettre à jour
     */
    protected update(instance: ModelBase, options?: SaveOptions) {
        this.update$(instance, this.fillDefaultSaveOptions(options)).subscribe(() => { });
    }

    public uploadAttachment$(parent: any, file: File, navigation: string = 'attachments'): Observable<any> {
        let url = this.routes['single'].replace('{id}', parent.id.toString()) + this.navigations[navigation].path;
        return this.uploadFile$(file, url);
    }

    /**
     * Méthode générique d'upload de fichier
     * @param file Le fichier à uploader
     * @param url L'URL de destination
     * @param async Si l'upload doit être fait en asynchrone ou pas
     * @returns
     */
    protected uploadFile$(file: File, url: string, options?: UploadOptions): Observable<any> {
        options = this.fillDefaultUploadOptions(options);
        // HTTP headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Disposition':
                    'attachment; filename="' + file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '') + '"',
                'Content-Type': file.type,
                'X-Background': (!!options.async).toString()
            }),
            reportProgress: true,
            observe: 'events' as 'events'
        };

        this._uploading$.next(true);
        return this.httpClient.post<any>(url, file, httpOptions).pipe(
            finalize(() => {
                this._uploading$.next(false);
            }),
            map((event: HttpEvent<any>) => {
                let progressArray = this._progressArray$.getValue();
                switch (event.type) {
                    case HttpEventType.Sent:
                        LOGGER.debug('Début de l\'upload');
                        // Add file to the upload progress table
                        let progress: UploadProgress = {
                            loaded: 0,
                            progress: 0,
                            total: 0,
                            fileName: file.name
                        };
                        progressArray.push(progress);
                        break;
                    case HttpEventType.ResponseHeader:
                        LOGGER.debug('Fin de l\'upload');
                        // Remove progress indicator from table
                        for (let i = 0; i < progressArray.length; i++) {
                            if (progressArray[i].fileName === file.name) {
                                progressArray.splice(i, 1);
                                break;
                            }
                        }
                        break;
                    case HttpEventType.UploadProgress:
                        let progressPct = Math.round((event.loaded / (event.total || event.loaded)) * 100);
                        LOGGER.debug(`Avancement : ${progressPct}%`);
                        // Update progress indicator
                        for (let i = 0; i < progressArray.length; i++) {
                            if (progressArray[i].fileName === file.name) {
                                progressArray[i].loaded = event.loaded;
                                progressArray[i].progress = progressPct;
                                progressArray[i].total = event.total || event.loaded;
                                break;
                            }
                        }
                        break;
                    case HttpEventType.Response:
                        let response = new ODataResponse(event.body);
                        if (!!options?.showMessages) {
                            if (!!options.successMessage) {
                                this.messageService.sendMessage({
                                    severity: 'success',
                                    summary: 'Upload réussi !',
                                    detail: options.successMessage,
                                    life: 5000
                                });
                            } else {
                                this.messageService.sendODataMessages(response.messages);
                            }
                        }
                        if (!!options?.emitEvent) {
                            this._uploaded$.next(file.name);
                        }
                        // Retour de l'instance
                        return response;
                }
                this._progressArray$.next(progressArray);
                return false;
            }),
            catchError(
                (error: any) => {
                    // Handle errors
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    /**
     * Méthode de validation d'un objet, c'est en fait un proxy qui appel la
     * méthode de mise à jour avec une autre URL
     * @param {any} instance L'objet à valider
     */
    public validate$(instance: ModelBase, options?: SaveOptions): Observable<any> {
        options = this.fillDefaultSaveOptions(options);
        // URL à utiliser
        options.url = this.routes.validate.replace('{id}', instance.id.toString());
        // Appel de la mise à jour
        return this.update$(instance, options).pipe(
            map(instance => {
                this._validated$.next(instance.id);
                return instance;
            })
        );
    };

    /**
     * Méthode de validation d'un objet, c'est en fait un proxy qui appel la
     * méthode de mise à jour avec une autre URL
     * @param {any} instance L'objet à valider
     */
    public validate(instance: ModelBase, options?: SaveOptions) {
        options = this.fillDefaultSaveOptions(options);
        // URL à utiliser
        options.url = this.routes.validate.replace('{id}', instance.id.toString());
        // Appel de la mise à jour
        this.validate$(instance, options).subscribe(() => { });
    }

    /**
     * Helper interne pour les endpoints d'action DELETE qui retournent l'instance
     * désérialisée — symétrique de {@link _postAction$}, pour les actions exposées
     * en POST/DELETE sur une même route (`{id}/issue-flag/`, `{id}/customer-label/`).
     *
     * ⚠️ Passer par ce helper plutôt que par `httpClient.delete` directement : c'est
     * lui qui relaie les messages OData et route les erreurs vers `handleError()`.
     * Un appel direct court-circuite les deux, et l'échec devient silencieux.
     *
     * @param url URL absolue (déjà construite avec l'id si nécessaire)
     * @returns L'instance désérialisée via `GeneralFactory.get(this.className, ...)`
     */
    protected _deleteAction$<T = any>(url: string): Observable<T> {
        return this.httpClient.delete(url).pipe(
            map((response: any) => this._mapActionResponse<T>(response)),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false as any as T);
                }
            )
        );
    }

    /**
     * Helper interne pour les endpoints d'action POST qui retournent l'instance
     * désérialisée (statut + reason + payload mis à jour). Utilisé par les
     * actions custom de chaque service (retry, abandon, replaceFile, etc.).
     *
     * ⚠️ Même règle que {@link _deleteAction$} : aucun service métier n'appelle
     * `httpClient` directement — messages et erreurs se gèrent ici, une seule fois.
     *
     * @param url URL absolue (déjà construite avec l'id si nécessaire)
     * @param body Body de la requête (défaut `{}`)
     * @returns L'instance désérialisée via `GeneralFactory.get(this.className, ...)`
     */
    protected _postAction$<T = any>(url: string, body: any = {}): Observable<T> {
        return this.httpClient.post(url, body).pipe(
            map((response: any) => this._mapActionResponse<T>(response)),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false as any as T);
                }
            )
        );
    }

    /**
     * Helper interne pour les endpoints d'action POST dont la valeur de réponse
     * n'est PAS une instance de la classe du service (ex. la correction matérielle
     * `{id}/material-correction/`, appelée sur un devis mais qui retourne une
     * ligne) : la valeur OData est renvoyée telle quelle, à charge pour l'appelant
     * de l'hydrater dans le bon modèle.
     *
     * ⚠️ Même règle que {@link _deleteAction$} : aucun service métier n'appelle
     * `httpClient` directement — messages et erreurs se gèrent ici, une seule fois.
     *
     * @param url URL absolue (déjà construite avec l'id si nécessaire)
     * @param body Body de la requête (défaut `{}`)
     * @returns La valeur brute de la réponse OData
     */
    protected _postActionValue$<T = any>(url: string, body: any = {}): Observable<T> {
        return this.httpClient.post(url, body).pipe(
            map((response: any) => {
                const odata = new ODataResponse(response);
                this.messageService.sendODataMessages(odata.messages);
                return odata.value as T;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false as any as T);
                }
            )
        );
    }

    /**
     * Helper interne pour les endpoints d'action PUT à corps léger qui retournent
     * l'instance désérialisée — symétrique de {@link _postAction$}, pour les actions
     * exposées en PUT (`{id}/close/`, `{id}/reopen/`, `{id}/cap/`).
     *
     * ⚠️ Même règle que {@link _deleteAction$} : aucun service métier n'appelle
     * `httpClient` directement — messages et erreurs se gèrent ici, une seule fois.
     * À la différence de {@link update$}, le corps est celui passé en paramètre,
     * pas la sérialisation complète de l'instance.
     *
     * @param url URL absolue (déjà construite avec l'id si nécessaire)
     * @param body Body de la requête (défaut `{}`)
     * @returns L'instance désérialisée via `GeneralFactory.get(this.className, ...)`
     */
    protected _putAction$<T = any>(url: string, body: any = {}): Observable<T> {
        return this.httpClient.put(url, body).pipe(
            map((response: any) => this._mapActionResponse<T>(response)),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false as any as T);
                }
            )
        );
    }

    /**
     * Helper interne pour les endpoints d'action PUT dont la valeur de réponse
     * n'est PAS une instance de la classe du service — symétrique de
     * {@link _postActionValue$} : la valeur OData est renvoyée telle quelle, à
     * charge pour l'appelant de l'interpréter.
     *
     * ⚠️ Même règle que {@link _deleteAction$} : aucun service métier n'appelle
     * `httpClient` directement — messages et erreurs se gèrent ici, une seule fois.
     *
     * @param url URL absolue (déjà construite avec l'id si nécessaire)
     * @param body Body de la requête (défaut `{}`)
     * @param relayMessages Relaie les messages de la réponse en toasts (défaut `true`) ;
     *        `false` pour un geste dont l'IHM porte déjà la confirmation. Les erreurs
     *        passent toujours par `handleError`.
     * @returns La valeur brute de la réponse OData
     */
    protected _putActionValue$<T = any>(url: string, body: any = {}, relayMessages: boolean = true): Observable<T> {
        return this.httpClient.put(url, body).pipe(
            map((response: any) => {
                const odata = new ODataResponse(response);
                if (relayMessages) {
                    this.messageService.sendODataMessages(odata.messages);
                }
                return odata.value as T;
            }),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false as any as T);
                }
            )
        );
    }

    /** Réponse d'un endpoint d'action : messages relayés, instance réhydratée. */
    private _mapActionResponse<T = any>(response: any): T {
        const odata = new ODataResponse(response);
        this.messageService.sendODataMessages(odata.messages);
        return GeneralFactory.get(this.className, odata.value) as T;
    }
}
